import React from 'react';
import {
    Box,
    Button,
    Typography,
    Container,
    Stack,
    Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/userContext';

const NotFoundPage = () => {
    const navigate = useNavigate();
    const auth = useAuth();

    const [{ user, loading }] = auth || [{ user: null, loading: false }];

    if (loading) return null;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                background:
                    'radial-gradient(circle at top left, rgba(99,102,241,0.35) 0, transparent 55%), radial-gradient(circle at bottom right, rgba(34,211,238,0.35) 0, #020617 55%)',
            }}
        >
            <Container maxWidth="sm">
                <Box
                    sx={{
                        position: 'relative',
                        p: { xs: 3, md: 4 },
                        borderRadius: 4,
                        bgcolor: 'rgba(15,23,42,0.75)', // slate-900-ish
                        border: '1px solid rgba(148,163,184,0.45)',
                        boxShadow: '0 24px 60px rgba(15,23,42,0.9)',
                        backdropFilter: 'blur(18px)',
                        color: 'common.white',
                        overflow: 'hidden',
                    }}
                >
                    {/* subtle glowing orbs */}
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            pointerEvents: 'none',
                            '&::before, &::after': {
                                content: '""',
                                position: 'absolute',
                                borderRadius: '999px',
                                filter: 'blur(32px)',
                                opacity: 0.4,
                            },
                            '&::before': {
                                width: 180,
                                height: 180,
                                background: 'rgba(94,234,212,0.9)',
                                top: -60,
                                right: -40,
                            },
                            '&::after': {
                                width: 160,
                                height: 160,
                                background: 'rgba(129,140,248,0.9)',
                                bottom: -40,
                                left: -40,
                            },
                        }}
                    />

                    <Stack spacing={2.5} sx={{ position: 'relative' }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Chip
                                size="small"
                                label="CompliSense"
                                sx={{
                                    bgcolor: 'rgba(15,23,42,0.9)',
                                    borderRadius: 999,
                                    border: '1px solid rgba(148,163,184,0.6)',
                                    color: 'rgba(226,232,240,0.9)',
                                    fontSize: 12,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.12,
                                }}
                            />
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                Secure compliance workspace
                            </Typography>
                        </Stack>

                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: { xs: '4rem', md: '5rem' },
                                fontWeight: 800,
                                lineHeight: 1,
                                letterSpacing: '-0.06em',
                            }}
                        >
                            404
                        </Typography>

                        <Typography variant="h5" fontWeight={700}>
                            This route is out of compliance.
                        </Typography>

                        <Typography variant="body2" sx={{ opacity: 0.85 }}>
                            The page you’re trying to access either doesn’t exist or isn’t part of
                            your current access profile. Check the URL, or head back to a safe
                            section of the app.
                        </Typography>

                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.5}
                            sx={{ mt: 1 }}
                        >
                            {user && (
                                <Button
                                    fullWidth
                                    sx={{
                                        py: 1.1,
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        background:
                                            'linear-gradient(135deg, #6366F1 0%, #22D3EE 100%)',
                                        color: 'common.white',
                                        boxShadow: '0 14px 30px rgba(79,70,229,0.55)',
                                        '&:hover': {
                                            filter: 'brightness(0.96)',
                                            boxShadow: '0 18px 40px rgba(56,189,248,0.55)',
                                        },
                                    }}
                                    onClick={() => navigate('/dashboard')}
                                >
                                    Go to dashboard
                                </Button>
                            )}

                            <Button
                                fullWidth
                                variant={user ? 'outlined' : 'contained'}
                                sx={{
                                    py: 1.1,
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    borderColor: 'rgba(148,163,184,0.7)',
                                    color: user ? 'rgba(226,232,240,0.9)' : 'common.white',
                                    background: user
                                        ? 'transparent'
                                        : 'linear-gradient(135deg, #0f172a 0%, #020617 100%)',
                                    '&:hover': {
                                        borderColor: 'rgba(148,163,184,1)',
                                        background: user
                                            ? 'rgba(15,23,42,0.85)'
                                            : 'linear-gradient(135deg, #020617 0%, #020617 100%)',
                                    },
                                }}
                                onClick={() => navigate('/login')}
                            >
                                Back to login
                            </Button>
                        </Stack>

                        <Typography
                            variant="caption"
                            sx={{ opacity: 0.55, mt: 1, textAlign: 'left' }}
                        >
                            If you believe this route should be accessible, please contact your
                            compliance administrator.
                        </Typography>
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
};

export default NotFoundPage;
